import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, InputAdornment, Container,
  IconButton, Snackbar, Alert,
} from "@mui/material";
import GridViewIcon from "@mui/icons-material/GridView";
import ListIcon from "@mui/icons-material/List";
import ListView from "../categories/ListView";
import { Link } from "react-router-dom";
import { Search } from "@mui/icons-material";
import HomeServices from "../../components/home/Services";
import { connect } from "react-redux";
import * as Actions from "../../redux/actions/";
import { Helmet } from "react-helmet-async";

const ServiceCard = ({ item }) => (
  <Box
    component={Link}
    to={`/services/${item.id}`}
    sx={{ textDecoration: "none", display: "block" }}
  >
    <Box
      sx={{
        backgroundColor: "#1a1a1a",
        border: "1px solid #2a2a2a",
        borderRadius: "12px",
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.25s ease",
        "&:hover": {
          border: "1px solid #FFC001",
          transform: "translateY(-3px)",
          boxShadow: "0 8px 24px rgba(255,192,1,0.1)",
          "& .card-name": { color: "#FFC001" },
        },
      }}
    >
      <Box sx={{ height: "180px", overflow: "hidden" }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            backgroundImage: `url(${item.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            transition: "transform 0.3s ease",
            "&:hover": { transform: "scale(1.05)" },
          }}
        />
      </Box>
      <Box sx={{ p: 1.5 }}>
        <Typography
          className="card-name"
          noWrap
          sx={{ color: "#fff", fontWeight: 700, fontSize: "14px", transition: "color 0.2s" }}
        >
          {item.name}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const Services = ({ services, loadServices, loadSubServices, error }) => {
  const [search, setSearch] = useState("");
  const [view, setView] = useState("Grid");
  const [snackbarStates, setSnackbarStates] = useState({ open: false, message: "", severity: "" });

  useEffect(() => { loadServices(); }, [loadServices]);

  useEffect(() => {
    if (services.length > 0) loadSubServices(services[0].id);
    if (error !== "") setSnackbarStates({ open: true, message: error, severity: "error" });
  }, [services, loadSubServices]);

  const filtered = services.filter((item) =>
    search === "" || item.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <meta property="og:image" content="https://prodappdata.s3.amazonaws.com/preview-images/services-preview.png" />
        <meta property="og:title" content="Services" />
        <meta property="og:description" content="Moore Exotics Services" />
      </Helmet>

      <Snackbar open={snackbarStates.open} autoHideDuration={3000} onClose={() => setSnackbarStates((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert onClose={() => setSnackbarStates((s) => ({ ...s, open: false }))} variant="filled" severity={snackbarStates.severity} sx={{ width: "100%" }}>
          {snackbarStates.message}
        </Alert>
      </Snackbar>

      <Box sx={{ backgroundColor: "#111" }}>
        {/* HomeServices hero — text + image section */}
        <HomeServices showButton={false} />

        {/* Hero header — matches Categories.js */}
        <Box
          sx={{
            position: "relative",
            background: "linear-gradient(to bottom, #1a1a1a 0%, #111 100%)",
            borderBottom: "1px solid #222",
            py: { xs: 5, md: 7 },
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "radial-gradient(ellipse at 50% 0%, rgba(255,192,1,0.07) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
              <Typography component={Link} to="/" sx={{ color: "#777", fontSize: "13px", textDecoration: "none", "&:hover": { color: "#FFC001" } }}>Home</Typography>
              <Typography sx={{ color: "#555", fontSize: "13px" }}>/</Typography>
              <Typography sx={{ color: "#FFC001", fontSize: "13px", fontWeight: 600 }}>Services</Typography>
            </Box>

            {/* Title */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography sx={{ fontWeight: 800, fontSize: { xs: "28px", sm: "36px", md: "48px" }, color: "#fff", lineHeight: 1.1, mb: 1 }}>
                Community{" "}
                <Box component="span" sx={{ color: "#FFC001" }}>Services</Box>
              </Typography>
              <Typography sx={{ color: "#777", fontSize: { xs: "13px", md: "15px" } }}>
                Browse expert services for your exotic animals
              </Typography>
            </Box>

            {/* Search + view toggle */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
              <TextField
                placeholder="Search services..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: "420px" },
                  backgroundColor: "#1a1a1a",
                  borderRadius: "10px",
                  "& .MuiOutlinedInput-root": {
                    paddingRight: 0,
                    color: "#fff",
                    borderRadius: "10px",
                    "& fieldset": { borderColor: "#333" },
                    "&:hover fieldset": { borderColor: "#FFC001" },
                    "&.Mui-focused fieldset": { borderColor: "#FFC001" },
                  },
                  "& .MuiInputBase-input::placeholder": { color: "#666", opacity: 1 },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ backgroundColor: "#FFC001", height: "40px", width: "44px", display: "flex", alignItems: "center", justifyContent: "center", borderTopRightRadius: "10px", borderBottomRightRadius: "10px", cursor: "pointer" }}>
                        <Search sx={{ color: "#000", fontSize: 20 }} />
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton onClick={() => setView("Grid")} sx={{ color: view === "Grid" ? "#FFC001" : "#555", "&:hover": { color: "#FFC001" } }}>
                  <GridViewIcon />
                </IconButton>
                <IconButton onClick={() => setView("List")} sx={{ color: view !== "Grid" ? "#FFC001" : "#555", "&:hover": { color: "#FFC001" } }}>
                  <ListIcon />
                </IconButton>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Grid / List */}
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          {filtered.length > 0 && (
            <Typography sx={{ color: "#555", fontSize: "13px", mb: 2 }}>
              {filtered.length} service{filtered.length !== 1 ? "s" : ""}
            </Typography>
          )}

          {view === "Grid" ? (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "repeat(2, 1fr)",
                  sm: "repeat(3, 1fr)",
                  md: "repeat(4, 1fr)",
                  lg: "repeat(5, 1fr)",
                },
              }}
            >
              {filtered.map((item) => (
                <ServiceCard key={item.id} item={item} />
              ))}
            </Box>
          ) : (
            <ListView categories={filtered} page="Services" search={search} />
          )}
        </Container>
      </Box>
    </>
  );
};

const mapStateToProps = ({ Services }) => ({
  services: Services.services,
  subServices: Services.subServices,
  error: Services.error,
});

const mapDispatchToProps = (dispatch) => ({
  loadServices: () => dispatch(Actions.thunkLoadServices()),
  loadSubServices: (serviceId) => dispatch(Actions.thunkLoadSubServices(serviceId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Services);
